import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Grid, FormControl, InputLabel, Select, MenuItem,
  TablePagination, InputAdornment, Switch, FormControlLabel,
  Autocomplete, Tooltip, Stack, Checkbox
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  CloudUpload as ImportIcon,
  CloudDownload as ExportIcon,
  EditNote as BulkEditIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  createWholesaleCustomer,
  updateWholesaleCustomer,
  deleteWholesaleCustomer,
  bulkImportWholesaleCustomers,
  bulkUpdateWholesaleCustomers,
  exportWholesaleCustomersCsv,
} from '../../services/retailOrdersService';
import api from '../../config/api';
import EntityActionsMenu from '../common/EntityActionsMenu';
import BulkImportDialog from '../common/BulkImportDialog';
import BulkUpdateDialog from '../common/BulkUpdateDialog';
import { listPriceTiers } from '../../services/inventoryService';
import { useUrlState } from '../../hooks/useUrlState';
import NewPriceTierDialog from '../price-tiers/NewPriceTierDialog';

// Sentinel option for the price-tier Autocomplete — selecting it opens the
// inline NewPriceTierDialog so the operator can create a new tier without
// leaving the customer form.
const CREATE_TIER_OPTION = { id: '__create__', tier_name: '+ Create new price tier', currency: '', __create: true };
const NONE_TIER_OPTION   = { id: 0, tier_name: '— None —', currency: '' };

// Parse a day-count out of a free-text payment term so the operator only enters
// it once (Payment Terms and Payment Due Days used to be filled separately and
// could silently drift — Rishabh 2026-07-20). "Net 60" / "60 days" → 60;
// "Due on receipt" / "COD" / "Prepaid" → 0; anything with no number → null
// (leave Due Days untouched so a manual value isn't wiped).
function dueDaysFromTerms(terms) {
  const s = String(terms || '').toLowerCase();
  if (/receipt|cod|on delivery|prepaid|due on|advance/.test(s)) return 0;
  const m = s.match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
}

// Additional ship-to addresses for one customer (multi-address Phase 1). The
// customer's Delivery Address above stays the "Primary" ship-to; these are
// alternates the operator can pick on the order/invoice (QuickBooks parity).
function CustomerAddresses({ customerId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null); // null=closed, {} = adding, {...row} = editing
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';
  const base = `${API_BASE}/api/wholesale-orders/customers/${customerId}/addresses`;

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(base); setRows(r.data?.data || []); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (customerId) load(); /* eslint-disable-next-line */ }, [customerId]);

  const blankAddr = { label: '', contact_name: '', phone: '', address_1: '', address_2: '', city: '', state: '', postal_code: '', country: '' };
  const saveDraft = async () => {
    setSaving(true); setErr(null);
    try {
      if (draft.id) await api.put(`${base}/${draft.id}`, draft);
      else await api.post(base, draft);
      setDraft(null); await load();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Remove address "${row.label || row.address_1 || 'this'}"?`)) return;
    setErr(null);
    try { await api.delete(`${base}/${row.id}`); await load(); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
  };
  const fld = (k) => ({ value: draft?.[k] ?? '', onChange: (e) => setDraft(d => ({ ...d, [k]: e.target.value })) });
  const oneLine = (r) => [r.address_1, r.address_2, r.city, r.state, r.postal_code, r.country].filter(Boolean).join(', ');

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Additional Ship-to Addresses</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setDraft({ ...blankAddr })} disabled={!!draft}>Add address</Button>
      </Box>
      <Typography variant="caption" color="text.secondary">
        The Delivery Address above is this customer's default ship-to. Add alternates here; you'll pick one on the order.
      </Typography>
      {err && <Alert severity="error" sx={{ my: 1 }} onClose={() => setErr(null)}>{err}</Alert>}
      {loading ? (
        <Box display="flex" justifyContent="center" py={2}><CircularProgress size={22} /></Box>
      ) : (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {rows.map(r => (
            <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.25, py: 0.75 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {r.label || '(no label)'}
                  {r.address_type && r.address_type !== 'shipping' && (
                    <Chip size="small" label={r.address_type} sx={{ ml: 1, height: 16, fontSize: 10 }} />
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {[r.contact_name, oneLine(r)].filter(Boolean).join(' — ') || '(empty address)'}
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <IconButton size="small" onClick={() => setDraft({ ...r })} disabled={!!draft}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => remove(r)} disabled={!!draft}><DeleteIcon fontSize="small" /></IconButton>
              </Box>
            </Box>
          ))}
          {rows.length === 0 && !draft && (
            <Typography variant="caption" color="text.secondary">No extra addresses yet.</Typography>
          )}
        </Stack>
      )}
      {draft && (
        <Box sx={{ mt: 1, p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Grid container spacing={1.25}>
            <Grid item xs={12} md={6}><TextField size="small" fullWidth label="Label (e.g. Oxford St store)" {...fld('label')} /></Grid>
            <Grid item xs={6} md={3}><TextField size="small" fullWidth label="Contact name" {...fld('contact_name')} /></Grid>
            <Grid item xs={6} md={3}><TextField size="small" fullWidth label="Phone" {...fld('phone')} /></Grid>
            <Grid item xs={12} md={6}><TextField size="small" fullWidth label="Address 1" {...fld('address_1')} /></Grid>
            <Grid item xs={12} md={6}><TextField size="small" fullWidth label="Address 2" {...fld('address_2')} /></Grid>
            <Grid item xs={6} md={3}><TextField size="small" fullWidth label="City" {...fld('city')} /></Grid>
            <Grid item xs={6} md={3}><TextField size="small" fullWidth label="State / Region" {...fld('state')} /></Grid>
            <Grid item xs={6} md={3}><TextField size="small" fullWidth label="Postal Code" {...fld('postal_code')} /></Grid>
            <Grid item xs={6} md={3}><TextField size="small" fullWidth label="Country" {...fld('country')} /></Grid>
          </Grid>
          <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 1 }}>
            <Button size="small" onClick={() => setDraft(null)} disabled={saving}>Cancel</Button>
            <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={saveDraft} disabled={saving}>
              {saving ? 'Saving…' : (draft.id ? 'Save address' : 'Add address')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

const blank = {
  qb_customer_id: '', customer_name: '', customer_type: 'retailer',
  price_tier_id: 0, subsidiary_id: 0, payment_terms: 'Net 30', payment_due_days: 30,
  send_method: 'qb', edi_party_id: '', default_warehouse_code: '', sales_rep: '', tax_rate: '',
  delivery_address_1: '', delivery_address_2: '', delivery_city: '', delivery_state: '',
  delivery_postal_code: '', delivery_country: '',
  billing_address_1: '', billing_city: '', billing_state: '', billing_postal_code: '', billing_country: '',
  contact_first_name: '', contact_last_name: '', contact_email: '', contact_phone: '',
  accounting_email: '',
  is_active: 1,
};

export default function CustomersAdmin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // URL-bound state so browser-back from CustomerHistory restores filters.
  const [search, setSearch] = useUrlState('q', '');
  // include_inactive toggle — backend defaults to active-only; this URL-bound
  // flag lets the operator pull deactivated customers into the list so they
  // can be reactivated. Previously deactivating a customer made them vanish
  // from this page with no way to recover them. Reported by Rishabh 2026-05-21.
  const [showInactive, setShowInactive] = useUrlState('inactive', '0');
  const [page, setPage] = useUrlState('p', 0, { parse: (v) => parseInt(v, 10) || 0 });
  const [rowsPerPage, setRowsPerPage] = useUrlState('pp', 25, { parse: (v) => parseInt(v, 10) || 25 });
  const skipFirstPageReset = useRef(true);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, object = edit
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  // Inline tier-create dialog (opened from the Price Tier Autocomplete option)
  const [newTierOpen, setNewTierOpen] = useState(false);

  // Delete-customer state
  const [deleteTarget, setDeleteTarget] = useState(null); // row being deleted (null = closed)
  const [deleting, setDeleting] = useState(false);
  const [deleteBlock, setDeleteBlock] = useState(null);   // { count, sample_order_ids } when 409
  const [markingInactive, setMarkingInactive] = useState(false);

  // Bulk actions state. selectedIds tracks cwe_id (which is the underlying
  // customer_wholesale_ext.id used by all bulk endpoints).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [importMode, setImportMode] = useState('insert');
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      // Bypass listWholesaleCustomers helper because the helper signature
      // doesn't accept params yet (services file is outside this bot's scope).
      // The backend accepts ?include_inactive=1 to return is_active=0 rows
      // alongside actives — used by the "Show inactive" toggle below.
      const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';
      const inactiveFlag = String(showInactive) === '1' ? 1 : undefined;
      const [c, t] = await Promise.all([
        api.get(`${API_BASE}/api/wholesale-orders/customers`, {
          params: inactiveFlag ? { include_inactive: 1 } : {}
        }),
        (async () => {
          try {
            const r = await listPriceTiers();
            return r.data || [];
          } catch { return []; }
        })()
      ]);
      setRows(c.data?.data || []);
      setTiers(t);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [showInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      (r.customer_name || '').toLowerCase().includes(q) ||
      (r.qb_customer_id || '').toLowerCase().includes(q) ||
      (r.tier_name || '').toLowerCase().includes(q) ||
      (r.delivery_city || '').toLowerCase().includes(q) ||
      (r.delivery_country || '').toLowerCase().includes(q) ||
      (r.contact_email || '').toLowerCase().includes(q)
    );
  }, [rows, search]);
  // Skip first run so hydrated `?p=N` survives mount.
  useEffect(() => {
    if (skipFirstPageReset.current) { skipFirstPageReset.current = false; return; }
    setPage(0);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
  const paged = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...blank, ...row });
    setError(null); setEditOpen(true);
  };
  const openCreate = () => {
    setEditing(null);
    setForm(blank);
    setError(null); setEditOpen(true);
  };

  // Deep-link from /contacts → "Add contact" → Customer opens this page with
  // ?new=1. Auto-trigger openCreate then strip the query so refresh doesn't
  // reopen the dialog.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') === '1') {
      openCreate();
      navigate('/retail/customers', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Deep-link from /contacts Edit icon: /retail/customers?id=<cwe_id>&edit=1
  // → find the matching row and open the edit dialog. Wait for `rows` to load
  // first; strip edit=1 after firing.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const targetId = p.get('id');
    if (!targetId || p.get('edit') !== '1' || !rows.length) return;
    const target = rows.find(r => String(r.cwe_id) === String(targetId));
    if (!target) return;
    openEdit(target);
    p.delete('edit');
    navigate({ search: p.toString() ? `?${p.toString()}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  // Deep-link from /contacts "Open canonical detail" on a customer row:
  // /retail/customers?id=<cwe_id>&from=contacts (no edit=1). Customers have
  // no separate detail page, so the most useful canonical view is their order
  // history. Resolve cwe_id → qb_customer_id once rows have loaded, then
  // redirect. Preserves from=contacts so the history page's Back button still
  // routes back to Contacts.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const targetId = p.get('id');
    if (!targetId || p.get('edit') === '1' || !rows.length) return;
    const target = rows.find(r => String(r.cwe_id) === String(targetId));
    if (!target || !target.qb_customer_id) return;
    const tail = p.get('from') === 'contacts' ? '?from=contacts' : '';
    navigate(
      `/retail/customers/${encodeURIComponent(target.qb_customer_id)}/history${tail}`,
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  const save = async () => {
    try {
      setSaving(true);
      if (editing) {
        await updateWholesaleCustomer(editing.cwe_id, form);
        setSuccess(`Saved ${form.customer_name || form.qb_customer_id}`);
      } else {
        const r = await createWholesaleCustomer(form);
        setSuccess(`Created customer #${r.id}`);
      }
      setEditOpen(false);
      await load();
      // If this edit dialog came from /contacts, return there after save.
      if (new URLSearchParams(location.search).get('from') === 'contacts') {
        navigate('/contacts');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  const openDelete = (row) => {
    setError(null); setDeleteTarget(row);
    setDeleteBlock(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteWholesaleCustomer(deleteTarget.cwe_id);
      setSuccess(`Deleted ${deleteTarget.customer_name || deleteTarget.qb_customer_id}`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      const status = e.response?.status;
      const body = e.response?.data;
      if (status === 409 && body?.code === 'customer_has_orders') {
        // Don't close — flip into the "blocked" view with order count + fallback CTA
        setDeleteBlock({ count: body.count, sample_order_ids: body.sample_order_ids || [] });
      } else {
        setError(body?.error || e.message);
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleMarkInactive = async () => {
    if (!deleteTarget) return;
    setMarkingInactive(true);
    try {
      await updateWholesaleCustomer(deleteTarget.cwe_id, { is_active: 0 });
      setSuccess(`Marked ${deleteTarget.customer_name || deleteTarget.qb_customer_id} inactive`);
      setDeleteTarget(null);
      setDeleteBlock(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setMarkingInactive(false);
    }
  };

  // === Bulk actions ====================================================

  const handleExport = async () => {
    try {
      const res = await exportWholesaleCustomersCsv();
      setSuccess(`Exported ${res.filename}`);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Export failed');
    }
  };

  const handleBulkImport = async (rowsIn) => {
    if (importMode === 'update') {
      const updates = rowsIn.map((r) => ({ ...r, id: parseInt(r.id, 10) }));
      const result = await bulkUpdateWholesaleCustomers(updates);
      await load();
      return result;
    }
    const result = await bulkImportWholesaleCustomers(rowsIn);
    await load();
    return result;
  };

  const handleBulkUpdate = async (updates) => {
    const result = await bulkUpdateWholesaleCustomers(updates);
    setSelectedIds(new Set());
    await load();
    return result;
  };

  const handleBulkDeactivate = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Deactivate ${ids.length} customer${ids.length === 1 ? '' : 's'}? They'll be hidden from pickers but order history is preserved. Reactivate later via Bulk Update → is_active.`)) return;
    try {
      const updates = ids.map((id) => ({ id, is_active: 0 }));
      const result = await bulkUpdateWholesaleCustomers(updates);
      setSuccess(`Deactivated ${result.updated ?? ids.length} customer${ids.length === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Bulk deactivate failed');
    }
  };

  // Column spec for the import dialog. Mirrors backend updateCustomer's
  // writable set (stringCols + intCols + decimalCols + customer_type/is_active).
  // customer_name is the only hard-required field for insert; everything else
  // has defaults in createCustomer.
  const CUSTOMER_IMPORT_COLUMNS = [
    { field: 'customer_name', label: 'Customer name', required: true,
      aliases: ['name', 'display_name', 'account', 'company'] },
    { field: 'customer_type', label: 'Type',
      enum: ['dtc', 'retailer', 'distributor'],
      aliases: ['type', 'category'] },
    { field: 'subsidiary_id', label: 'Subsidiary ID', type: 'number',
      aliases: ['subsidiary', 'sub_id'] },
    { field: 'price_tier_id', label: 'Price tier ID', type: 'number',
      aliases: ['tier_id', 'tier'] },
    { field: 'qb_customer_id', label: 'QuickBooks customer ID',
      aliases: ['qb_id', 'quickbooks_id'] },
    { field: 'edi_party_id', label: 'EDI party ID', aliases: ['edi_id', 'spsc'] },
    // Payment terms + early-pay/penalty (Decimal cols newly editable)
    { field: 'payment_terms', label: 'Payment terms', aliases: ['terms'] },
    { field: 'payment_due_days', label: 'Payment due days', type: 'number',
      aliases: ['due_days', 'net_days'] },
    { field: 'early_pay_discount_pct', label: 'Early-pay discount %', type: 'number',
      aliases: ['early_discount_pct', 'discount_pct'] },
    { field: 'early_pay_window_days', label: 'Early-pay window (days)', type: 'number',
      aliases: ['early_window'] },
    { field: 'short_fulfillment_penalty_pct', label: 'Short-fill penalty %', type: 'number',
      aliases: ['short_penalty_pct', 'penalty_pct'] },
    { field: 'retailer_margin_pct', label: 'Retailer margin %', type: 'number',
      aliases: ['margin_pct'] },
    { field: 'send_method', label: 'Send via', aliases: ['delivery_method'] },
    { field: 'default_warehouse_code', label: 'Default warehouse', aliases: ['warehouse_code'] },
    { field: 'sales_rep', label: 'Sales rep', aliases: ['rep'] },
    { field: 'tax_rate', label: 'Tax rate' },
    { field: 'tax_treatment', label: 'Tax treatment' },
    { field: 'incoterms', label: 'Incoterms' },
    // Delivery address (ship-to)
    { field: 'delivery_address_1', label: 'Delivery address 1', aliases: ['delivery_address', 'ship_to_1'] },
    { field: 'delivery_address_2', label: 'Delivery address 2', aliases: ['ship_to_2'] },
    { field: 'delivery_city', label: 'Delivery city', aliases: ['ship_city'] },
    { field: 'delivery_state', label: 'Delivery state', aliases: ['ship_state'] },
    { field: 'delivery_postal_code', label: 'Delivery postcode', aliases: ['ship_postcode', 'ship_zip'] },
    { field: 'delivery_country', label: 'Delivery country', aliases: ['ship_country'] },
    // Billing address (bill-to — often different for chains)
    { field: 'billing_address_1', label: 'Billing address 1', aliases: ['bill_to_1'] },
    { field: 'billing_city', label: 'Billing city', aliases: ['bill_city'] },
    { field: 'billing_state', label: 'Billing state', aliases: ['bill_state', 'bill_region'] },
    { field: 'billing_postal_code', label: 'Billing postcode', aliases: ['bill_postcode'] },
    { field: 'billing_country', label: 'Billing country', aliases: ['bill_country'] },
    // Contact
    { field: 'contact_first_name', label: 'Contact first name', aliases: ['first_name'] },
    { field: 'contact_last_name', label: 'Contact last name', aliases: ['last_name'] },
    { field: 'contact_email', label: 'Contact email', type: 'email', aliases: ['email'] },
    { field: 'accounting_email', label: 'Accounting email', type: 'email', aliases: ['invoice email', 'invoicing email', 'accounts email', 'billing email'] },
    { field: 'contact_phone', label: 'Contact phone', aliases: ['phone', 'tel'] },
    { field: 'special_terms_notes', label: 'Notes', aliases: ['notes', 'comments'] },
  ];

  const CUSTOMER_IMPORT_SAMPLE = [
    {
      customer_name: 'Selfridges London',
      customer_type: 'retailer',
      payment_terms: 'Net 60',
      payment_due_days: 60,
      send_method: 'qb',
      default_warehouse_code: 'EF-UK',
      sales_rep: 'Sarah K.',
      tax_treatment: 'VAT 20%',
      delivery_address_1: '400 Oxford Street',
      delivery_city: 'London',
      delivery_postal_code: 'W1A 1AB',
      delivery_country: 'GB',
      contact_first_name: 'James',
      contact_last_name: 'Reid',
      contact_email: 'james.reid@selfridges.example',
      contact_phone: '+44 20 7160 6222',
      special_terms_notes: 'EDI via SPS Commerce party SLF-001',
    },
    {
      customer_name: 'Costco Wholesale Australia',
      customer_type: 'distributor',
      payment_terms: 'Net 30',
      payment_due_days: 30,
      send_method: 'edi',
      default_warehouse_code: 'EF-AU',
      sales_rep: 'Mark D.',
      delivery_address_1: '17 Mentmore Cres',
      delivery_city: 'Sydney',
      delivery_state: 'NSW',
      delivery_postal_code: '2018',
      delivery_country: 'AU',
      contact_first_name: 'Priya',
      contact_last_name: 'N.',
      contact_email: 'priya.n@costco.example',
      contact_phone: '+61 2 9085 4000',
      special_terms_notes: '',
    },
  ];

  // Bulk-update editable fields. Per-row identity (customer_name) and address
  // rows excluded by intent — those are nearly always unique. Tax + early-pay
  // fields ARE included so a contracts change ("all Costco accounts move to
  // Net 60 + 2% early-pay") can be applied in one operation.
  const CUSTOMER_BULK_EDITABLE = [
    { field: 'customer_type', label: 'Type', type: 'enum', enum: ['dtc', 'retailer', 'distributor'] },
    { field: 'price_tier_id', label: 'Price tier ID', type: 'number',
      help: 'Set the tier_id from your Price Tiers admin.' },
    { field: 'subsidiary_id', label: 'Subsidiary ID', type: 'number' },
    { field: 'payment_terms', label: 'Payment terms', placeholder: 'e.g. Net 30' },
    { field: 'payment_due_days', label: 'Payment due days', type: 'number' },
    { field: 'early_pay_discount_pct', label: 'Early-pay discount %', type: 'number' },
    { field: 'early_pay_window_days', label: 'Early-pay window (days)', type: 'number' },
    { field: 'short_fulfillment_penalty_pct', label: 'Short-fill penalty %', type: 'number' },
    { field: 'retailer_margin_pct', label: 'Retailer margin %', type: 'number' },
    { field: 'send_method', label: 'Send via', placeholder: 'qb / edi / email' },
    { field: 'default_warehouse_code', label: 'Default warehouse code' },
    { field: 'sales_rep', label: 'Sales rep' },
    { field: 'incoterms', label: 'Incoterms' },
    { field: 'tax_rate', label: 'Tax rate' },
    { field: 'tax_treatment', label: 'Tax treatment' },
    { field: 'is_active', label: 'Status', type: 'boolean',
      help: 'Yes = active, No = soft-deactivated.' },
    { field: 'special_terms_notes', label: 'Notes', multiline: true },
  ];

  const tiersById = useMemo(() => {
    const m = new Map();
    tiers.forEach(t => m.set(t.id, t));
    return m;
  }, [tiers]);

  // Autocomplete option list: None + all tiers + Create-new sentinel
  const tierOptions = useMemo(
    () => [NONE_TIER_OPTION, ...tiers, CREATE_TIER_OPTION],
    [tiers]
  );

  const selectedTierOption = useMemo(() => {
    if (!form.price_tier_id) return NONE_TIER_OPTION;
    return tiersById.get(form.price_tier_id) || NONE_TIER_OPTION;
  }, [form.price_tier_id, tiersById]);

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Wholesale Customers</Typography>
          <Typography variant="body2" color="textSecondary">
            Retailers, distributors, and their ship-to addresses. Pick a row to edit; "Add Customer" for new accounts.
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Button startIcon={<AddIcon />} onClick={openCreate} variant="contained">Add Customer</Button>
          <EntityActionsMenu
            label="Actions"
            selectedCount={selectedIds.size}
            actions={[
              { key: 'import', label: 'Import new customers', hint: 'CSV or Excel — drag-drop + validation preview',
                icon: <ImportIcon fontSize="small" />, group: 'Data',
                onClick: () => { setImportMode('insert'); setImportOpen(true); } },
              { key: 'export', label: 'Export current view', hint: 'Downloads a CSV of all customers',
                icon: <ExportIcon fontSize="small" />, group: 'Data',
                onClick: handleExport },
              { key: 'update-from-csv', label: 'Update from CSV',
                hint: 'Export → edit in Excel → re-upload. Matches rows by id.',
                icon: <BulkEditIcon fontSize="small" />, group: 'Data',
                onClick: () => { setImportMode('update'); setImportOpen(true); } },
              { key: 'bulk-update', label: 'Bulk update selected',
                hint: 'Pick fields to overwrite, preview diff, apply',
                icon: <BulkEditIcon fontSize="small" />, group: 'Edit',
                requiresSelection: true,
                onClick: () => setBulkUpdateOpen(true) },
              { key: 'bulk-deactivate', label: 'Deactivate selected',
                hint: 'Soft-delete — preserves order history',
                icon: <DeleteIcon fontSize="small" />, group: 'Edit',
                requiresSelection: true, dangerous: true,
                onClick: handleBulkDeactivate },
            ]}
          />
          <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{filtered.length} customer{filtered.length === 1 ? '' : 's'}</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <FormControlLabel
                control={<Switch
                  size="small"
                  checked={String(showInactive) === '1'}
                  onChange={(e) => setShowInactive(e.target.checked ? '1' : '0')}
                />}
                label="Show inactive"
                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
              />
              <TextField size="small" placeholder="Search name, tier, city, email…"
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ width: 320 }} />
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedIds.size > 0 && selectedIds.size < filtered.length}
                      checked={filtered.length > 0 && selectedIds.size === filtered.length}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedIds(new Set(filtered.map(r => r.cwe_id)));
                        else setSelectedIds(new Set());
                      }}
                      title="Select all"
                    />
                  </TableCell>
                  <TableCell>Customer Name</TableCell>
                  <TableCell>Tier</TableCell>
                  <TableCell>City, Country</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Payment Terms</TableCell>
                  <TableCell>Send Via</TableCell>
                  {/* qb_customer_id — the permanent customer join key. Values
                      for ported customers came from CIN7, hence the old "CIN7
                      ID" label; renamed since CIN7 is retired (Rishabh 2026-07-06). */}
                  <TableCell>Customer ID</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map(r => (
                  <TableRow
                    key={r.cwe_id}
                    hover
                    selected={selectedIds.has(r.cwe_id)}
                    sx={Number(r.is_active) === 0 ? { opacity: 0.55 } : {}}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.has(r.cwe_id)}
                        onChange={(e) => {
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(r.cwe_id);
                            else next.delete(r.cwe_id);
                            return next;
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <strong>{r.customer_name || r.display_name}</strong>
                      {Number(r.is_active) === 0 && (
                        <Chip size="small" label="Inactive" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                      )}
                    </TableCell>
                    <TableCell><Chip size="small" label={r.tier_name || '—'} color={r.tier_name ? 'primary' : 'default'} /></TableCell>
                    <TableCell>{[r.delivery_city, r.delivery_country].filter(Boolean).join(', ') || '—'}</TableCell>
                    <TableCell>
                      {r.contact_first_name || r.contact_last_name ? (
                        <Box>
                          <Typography variant="body2">{r.contact_first_name} {r.contact_last_name}</Typography>
                          {r.contact_email && <Typography variant="caption" color="textSecondary">{r.contact_email}</Typography>}
                        </Box>
                      ) : '—'}
                    </TableCell>
                    <TableCell>{r.payment_terms || '—'}</TableCell>
                    <TableCell><Chip size="small" label={r.send_method || 'qb'} /></TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{(r.qb_customer_id || '').replace(/^CIN7-/, '')}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View order history">
                        <IconButton size="small" onClick={() => navigate(`/retail/customers/${encodeURIComponent(r.qb_customer_id)}/history`)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete (only if no order history)">
                        <IconButton size="small" onClick={() => openDelete(r)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {paged.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center">
                    <Typography color="textSecondary" sx={{ py: 2 }}>No customers match.</Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={filtered.length} page={page}
            onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]} />
        </CardContent>
      </Card>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.customer_name || editing.qb_customer_id}` : 'Add Customer'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="Customer Name *" value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                helperText='Full retailer name (use " - Ship-to-name" suffix for multiple addresses of the same retailer)' />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Customer ID" value={form.qb_customer_id}
                onChange={(e) => setForm({ ...form, qb_customer_id: e.target.value })}
                disabled={!!editing} helperText={editing ? 'Cannot change — permanent ID linking this customer to orders, invoices and QuickBooks' : 'Optional — auto-generates as NEW-N if blank'} />
            </Grid>

            <Grid item xs={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Customer Type</InputLabel>
                <Select value={form.customer_type} label="Customer Type"
                  onChange={(e) => setForm({ ...form, customer_type: e.target.value })}>
                  <MenuItem value="dtc">DTC</MenuItem>
                  <MenuItem value="retailer">Retailer</MenuItem>
                  <MenuItem value="distributor">Distributor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={4}>
              <Autocomplete
                value={selectedTierOption}
                options={tierOptions}
                disableClearable
                getOptionLabel={(o) => o?.tier_name || ''}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                onChange={(_, opt) => {
                  if (!opt) return;
                  if (opt.__create) { setNewTierOpen(true); return; }
                  setForm({ ...form, price_tier_id: opt.id || 0 });
                }}
                renderOption={(props, option) => {
                  if (option.__create) {
                    return (
                      <li {...props} key="__create__" style={{ ...props.style, borderTop: '1px solid #eee', color: '#1976d2', fontWeight: 500 }}>
                        <AddIcon fontSize="small" style={{ marginRight: 6 }} />
                        Create new price tier…
                      </li>
                    );
                  }
                  if (option.id === 0) {
                    return <li {...props} key="__none__" style={{ ...props.style, color: '#888' }}>— None —</li>;
                  }
                  return (
                    <li {...props} key={option.id}>
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <span>{option.tier_name}</span>
                        <Chip size="small" label={option.currency} sx={{ ml: 1, height: 18, fontSize: 10 }} />
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Price Tier"
                    helperText={selectedTierOption?.currency ? `Pricing in ${selectedTierOption.currency}` : 'No tier selected — orders priced via Shopify fallback'} />
                )}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Send Via</InputLabel>
                <Select value={form.send_method} label="Send Via"
                  onChange={(e) => setForm({ ...form, send_method: e.target.value })}>
                  <MenuItem value="qb">QuickBooks</MenuItem>
                  <MenuItem value="sps_commerce">SPS Commerce</MenuItem>
                  <MenuItem value="procuros">Procuros</MenuItem>
                  <MenuItem value="thg_procure">THG Procure</MenuItem>
                  <MenuItem value="portal">Portal</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} md={4}>
              <TextField fullWidth label="EDI Party ID"
                value={form.edi_party_id || ''}
                onChange={(e) => setForm({ ...form, edi_party_id: e.target.value })}
                placeholder="e.g. Myer Australia"
                helperText="Trading-partner name as it appears on incoming EDI (sps_documents.trading_partner)" />
            </Grid>

            <Grid item xs={6} md={4}>
              <TextField fullWidth label="Payment Terms" value={form.payment_terms}
                onChange={(e) => {
                  const v = e.target.value;
                  const d = dueDaysFromTerms(v);
                  // Auto-fill Due Days from the term when a number is parseable;
                  // leave it alone otherwise so a manual value isn't clobbered.
                  setForm((f) => ({ ...f, payment_terms: v, ...(d !== null ? { payment_due_days: d } : {}) }));
                }}
                placeholder="e.g. Net 30, 30 days from invoice" />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField fullWidth label="Payment Due Days" type="number" value={form.payment_due_days}
                onChange={(e) => setForm({ ...form, payment_due_days: e.target.value })}
                helperText="Auto-filled from Payment Terms — edit if it differs" />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField fullWidth label="Tax Rate" value={form.tax_rate}
                onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                placeholder="e.g. 20%, Zero Rated EC Goods Income" />
            </Grid>

            <Grid item xs={6} md={4}>
              <TextField fullWidth label="Default Warehouse Code" value={form.default_warehouse_code}
                onChange={(e) => setForm({ ...form, default_warehouse_code: e.target.value })}
                placeholder="leave blank → auto by geography" />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField fullWidth label="Subsidiary ID" type="number" value={form.subsidiary_id}
                onChange={(e) => setForm({ ...form, subsidiary_id: e.target.value })} />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField fullWidth label="Sales Rep" value={form.sales_rep}
                onChange={(e) => setForm({ ...form, sales_rep: e.target.value })} />
            </Grid>

            <Grid item xs={12}><Typography variant="subtitle2" sx={{ mt: 1, color: 'text.secondary' }}>Contact</Typography></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="First Name" value={form.contact_first_name} onChange={(e) => setForm({ ...form, contact_first_name: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="Last Name" value={form.contact_last_name} onChange={(e) => setForm({ ...form, contact_last_name: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="POC email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="Phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></Grid>
            {/* Separate invoice-recipient address — customers often have a POC
                email AND an accounts email (Rishabh 2026-07-06). */}
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Accounting email (invoices)" type="email"
                value={form.accounting_email}
                helperText="Where invoices should be sent, if different from the POC email"
                onChange={(e) => setForm({ ...form, accounting_email: e.target.value })} />
            </Grid>

            <Grid item xs={12}><Typography variant="subtitle2" sx={{ mt: 1, color: 'text.secondary' }}>Delivery Address</Typography></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Address 1" value={form.delivery_address_1} onChange={(e) => setForm({ ...form, delivery_address_1: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Address 2" value={form.delivery_address_2} onChange={(e) => setForm({ ...form, delivery_address_2: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="City" value={form.delivery_city} onChange={(e) => setForm({ ...form, delivery_city: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="State / Region" value={form.delivery_state} onChange={(e) => setForm({ ...form, delivery_state: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="Postal Code" value={form.delivery_postal_code} onChange={(e) => setForm({ ...form, delivery_postal_code: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="Country" value={form.delivery_country} onChange={(e) => setForm({ ...form, delivery_country: e.target.value })} /></Grid>

            <Grid item xs={12}><Typography variant="subtitle2" sx={{ mt: 1, color: 'text.secondary' }}>Billing Address (if different)</Typography></Grid>
            <Grid item xs={12}><TextField fullWidth label="Address 1" value={form.billing_address_1} onChange={(e) => setForm({ ...form, billing_address_1: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="City" value={form.billing_city} onChange={(e) => setForm({ ...form, billing_city: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="State / Region" value={form.billing_state} onChange={(e) => setForm({ ...form, billing_state: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="Postal Code" value={form.billing_postal_code} onChange={(e) => setForm({ ...form, billing_postal_code: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="Country" value={form.billing_country} onChange={(e) => setForm({ ...form, billing_country: e.target.value })} /></Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
              {editing ? (
                <CustomerAddresses customerId={editing.cwe_id} />
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Additional Ship-to Addresses</Typography>
                  <Typography variant="caption" color="text.secondary">Save this customer first, then reopen to add extra ship-to addresses.</Typography>
                </>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} />}
                label={form.is_active ? 'Active — appears in pickers' : 'Inactive — hidden from pickers'} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} variant="contained" startIcon={<SaveIcon />} disabled={saving}>
            {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create Customer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inline "Create new price tier" dialog — auto-selects the new tier on success */}
      <NewPriceTierDialog
        open={newTierOpen}
        onClose={() => setNewTierOpen(false)}
        onSaved={async (newTierId) => {
          setNewTierOpen(false);
          // Refresh tiers and select the new one on the customer form
          try {
            const r = await listPriceTiers();
            setTiers(r.data || []);
          } catch { /* keep going — selection still works once list refreshes */ }
          setForm((f) => ({ ...f, price_tier_id: newTierId }));
        }}
      />

      {/* Delete customer confirmation — also handles the 409 "has orders" case */}
      <Dialog
        open={!!deleteTarget}
        onClose={(deleting || markingInactive) ? undefined : () => { setDeleteTarget(null); setDeleteBlock(null); }}
        maxWidth="xs" fullWidth
      >
        <DialogTitle>
          {deleteBlock ? 'Cannot delete this customer' : 'Delete customer?'}
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {!deleteBlock ? (
            <Stack spacing={1}>
              <Typography>
                Delete <strong>{deleteTarget?.customer_name || deleteTarget?.qb_customer_id}</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The customer will be hidden from pickers and order history. The underlying record stays for audit. This is only allowed when the customer has no order history.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <WarningIcon color="warning" />
                <Typography>
                  <strong>{deleteTarget?.customer_name}</strong> has <strong>{deleteBlock.count}</strong> non-voided order{deleteBlock.count === 1 ? '' : 's'} and cannot be deleted.
                </Typography>
              </Box>
              {deleteBlock.sample_order_ids?.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Sample order IDs: {deleteBlock.sample_order_ids.map(id => String(id).replace(/^CIN7-/, '')).join(', ')}
                  {deleteBlock.count > deleteBlock.sample_order_ids.length && ` (+${deleteBlock.count - deleteBlock.sample_order_ids.length} more)`}
                </Typography>
              )}
              <Alert severity="info" sx={{ mt: 1 }}>
                You can mark the customer inactive instead — they'll disappear from pickers but order history stays linked.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {!deleteBlock ? (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
              <Button onClick={confirmDelete} color="error" variant="contained"
                startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => { setDeleteTarget(null); setDeleteBlock(null); }} disabled={markingInactive}>Close</Button>
              <Button onClick={handleMarkInactive} variant="contained"
                startIcon={markingInactive ? <CircularProgress size={16} color="inherit" /> : null}
                disabled={markingInactive || !deleteTarget?.is_active}
              >
                {markingInactive
                  ? 'Marking inactive…'
                  : deleteTarget?.is_active === 0
                    ? 'Already inactive'
                    : 'Mark inactive instead'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <BulkImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        entityLabel="customer"
        mode={importMode}
        columnSpec={CUSTOMER_IMPORT_COLUMNS}
        sampleRows={CUSTOMER_IMPORT_SAMPLE}
        onSubmit={handleBulkImport}
        helpText={
          importMode === 'update'
            ? 'Round-trip workflow: keep the id column, edit any other cells, leave blank to skip. Type __CLEAR__ to explicitly empty a cell.'
            : 'Required: customer_name. Header row required; column order doesn\'t matter; headers matched case-insensitively (e.g. \'Display Name\' → customer_name).'
        }
      />

      <BulkUpdateDialog
        open={bulkUpdateOpen}
        onClose={() => setBulkUpdateOpen(false)}
        entityLabel="customer"
        selectedRows={filtered.filter(r => selectedIds.has(r.cwe_id)).map(r => ({ ...r, id: r.cwe_id }))}
        editableFields={CUSTOMER_BULK_EDITABLE}
        identityColumns={['customer_name', 'delivery_city', 'delivery_country']}
        onSubmit={handleBulkUpdate}
      />
    </Box>
  );
}
